import { Link } from "react-router-dom";

const headings = {
  ar: {
    howTo: "طريقة الاستخدام",
    features: "المميزات",
    limitations: "القيود",
    supported: "المحتوى المدعوم",
    faq: "الأسئلة الشائعة",
    troubleshooting: "المشاكل والحلول",
    related: "أدوات ذات صلة",
  },
  en: {
    howTo: "How to use",
    features: "Features",
    limitations: "Limitations",
    supported: "Supported content",
    faq: "Frequently asked questions",
    troubleshooting: "Troubleshooting",
    related: "Related tools",
  },
};

function StringSection({ title, items, ordered = false }) {
  if (!items?.length) return null;
  const List = ordered ? "ol" : "ul";
  return (
    <section className="space-y-3">
      <h2 className="font-head text-xl font-bold">{title}</h2>
      <List className={`${ordered ? "list-decimal" : "list-disc"} space-y-2 ps-6 text-muted-foreground`}>
        {items.filter((item) => typeof item === "string" && item.trim()).map((item, index) => (
          <li key={`${title}-${index}`} className="whitespace-pre-line leading-7">{item}</li>
        ))}
      </List>
    </section>
  );
}

function PairSection({ title, items, firstKey, secondKey }) {
  const complete = (items || []).filter((item) => item?.[firstKey]?.trim() && item?.[secondKey]?.trim());
  if (!complete.length) return null;
  return (
    <section className="space-y-3">
      <h2 className="font-head text-xl font-bold">{title}</h2>
      <div className="space-y-4">
        {complete.map((item, index) => (
          <div key={`${title}-${index}`} className="rounded-xl border border-border p-4">
            <h3 className="font-semibold">{item[firstKey]}</h3>
            <p className="mt-2 whitespace-pre-line leading-7 text-muted-foreground">{item[secondKey]}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function ToolContent({ content, tools = [], lang = "ar", showHeading = false }) {
  if (!content) return null;
  const labels = headings[lang] || headings.ar;
  const sections = [...(content.sections || [])].filter((section) => (
    section?.title?.trim() || section?.heading?.trim() || section?.content?.trim() || section?.body?.trim()
  ));
  const relatedTools = tools.filter((tool) => content.related_tools?.includes(tool.key) && tool.enabled !== false);

  return (
    <article className="space-y-7 text-start" data-testid="tool-content">
      {showHeading && content.heading && <h1 className="font-head text-3xl font-extrabold">{content.heading}</h1>}
      {content.intro && <p className="leading-8 text-muted-foreground">{content.intro}</p>}
      {content.detailed_content && <div className="whitespace-pre-line leading-8 text-muted-foreground">{content.detailed_content}</div>}

      {sections.map((section, index) => (
        <section key={`section-${index}`} className="space-y-2">
          <h2 className="font-head text-xl font-bold">{section.title || section.heading}</h2>
          <p className="whitespace-pre-line leading-8 text-muted-foreground">{section.content || section.body}</p>
        </section>
      ))}

      <StringSection title={labels.howTo} items={content.how_to} ordered />
      <StringSection title={labels.features} items={content.features} />
      <StringSection title={labels.limitations} items={content.limitations} />
      <StringSection title={labels.supported} items={content.supported_content} />
      <PairSection title={labels.faq} items={content.faq} firstKey="question" secondKey="answer" />
      <PairSection title={labels.troubleshooting} items={content.troubleshooting} firstKey="issue" secondKey="solution" />

      {relatedTools.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-head text-xl font-bold">{labels.related}</h2>
          <ul className="flex flex-wrap gap-3">
            {relatedTools.map((tool) => (
              <li key={tool.key}><Link className="text-primary hover:underline" to={`/${tool.slug}`}>{tool.title}</Link></li>
            ))}
          </ul>
        </section>
      )}
    </article>
  );
}
